import React, { useCallback, useEffect, useState } from 'react'
import { Button, Card, Col, Form, Input, Row, Select, Space, Spin, message } from 'antd'
import axios from 'axios'
import { OrganisationFacturationCgv } from './OrganisationFacturationCgv'

export const SPECIALITE_OPTIONS = [
  'Fabricant',
  'Réseau',
  'Négoce',
  'Peinture',
  'Isolation',
  'Menuiserie',
  'Électricité',
  'Chauffage',
  'Bois bûche - Pellet',
  'Sanitaire',
  'Cuisine',
  'Piscine',
  'Couverture - Étanchéité',
  'Bois - Panneau',
  'Quincaillerie',
  'TP',
  'Revêtement',
  'Protection Solaire',
  'ENR',
  'Service',
  'Partenaire',
  'Amex',
  'EPI',
  'Équipement - Accessoire',
  'Domotique',
  'Aménagement intérieur',
]

export const ENSEIGNE_OPTIONS = [
  'Tout Faire',
  'Gédimat',
  'Bigmat',
  'France Matériaux',
  'Starmat',
  'Mat+',
  'Algorel',
  'SOCODA',
  'Comptoir de l\'Ours',
  'Partelec-Siele',
  'Espace Revêtements',
  'Agir',
  'SOFRAMAP',
  'Gédibois',
  'Nebopan',
  'UGD',
  'Les Matériaux',
  'Espace Aubade',
]

const SIRET_PATTERN = /^\d{14}$/

interface OrganisationParamsData {
  orga_id: string
  name: string
  product_global_name: string
  siret: string | null
  specialites: string[]
  enseignes: string[]
}

interface OrganisationParamsFormValues {
  name: string
  product_global_name: string
  siret?: string
  specialites: string[]
  enseignes: string[]
}

interface SaveResponse {
  ok: boolean
  errors?: string
}

interface OrganisationParamsProps {
  orgaId: string
}

export function OrganisationParams({ orgaId }: OrganisationParamsProps) {
  const [form] = Form.useForm<OrganisationParamsFormValues>()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [orgaSiret, setOrgaSiret] = useState<string | null>(null)

  const loadParams = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await axios.get<OrganisationParamsData>(`/organisations/${orgaId}/params/data`)
      form.setFieldsValue({
        name: data.name,
        product_global_name: data.product_global_name,
        siret: data.siret || '',
        specialites: data.specialites || [],
        enseignes: data.enseignes || [],
      })
      setOrgaSiret(data.siret)
    } catch (error) {
      console.error(error)
      message.error("Impossible de charger les paramètres de l'organisation")
    } finally {
      setLoading(false)
    }
  }, [form, orgaId])

  useEffect(() => {
    loadParams()
  }, [loadParams])

  const onFinish = async (values: OrganisationParamsFormValues) => {
    setSaving(true)
    try {
      const siret = (values.siret || '').trim()
      const { data } = await axios.post<SaveResponse>(
        `/organisations/${orgaId}/params`,
        {
          name: values.name,
          product_global_name: values.product_global_name,
          siret: siret || null,
          specialites: values.specialites || [],
          enseignes: values.enseignes || [],
        },
        { validateStatus: status => status < 500 },
      )

      if (data.ok) {
        message.success('Organisation enregistrée')
        setOrgaSiret(siret || null)
        return
      }

      message.error(data.errors || "Erreur lors de l'enregistrement")
    } catch (error) {
      console.error(error)
      message.error("Erreur lors de l'enregistrement")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div style={{ marginTop: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }

  return (
    <div style={{ margin: '16px 0' }}>
      <Row gutter={[84, 24]} align="top">
        <Col xs={24} lg={10} xl={10}>
          <Card size="small" title="Paramètres de l'organisation">
            <Form form={form} layout="vertical" onFinish={onFinish} requiredMark>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: 'Nom requis' }]}
                className="no-box-shadow"
              >
                <Input autoFocus />
              </Form.Item>

              <Form.Item
                label="Nom du produit (ex. : Loom)"
                name="product_global_name"
                rules={[{ required: true, message: 'Nom du produit requis' }]}
                className="no-box-shadow"
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="SIRET"
                name="siret"
                rules={[
                  {
                    validator: async (_, value) => {
                      const siret = (value || '').trim()
                      if (!siret) return
                      if (!SIRET_PATTERN.test(siret)) {
                        throw new Error('Le SIRET doit contenir exactement 14 chiffres')
                      }
                    },
                  },
                ]}
                className="no-box-shadow"
              >
                <Input maxLength={14} placeholder="14 chiffres (optionnel)" inputMode="numeric" />
              </Form.Item>

              <Form.Item label="Spécialité" name="specialites" className="no-box-shadow">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Sélectionner une ou plusieurs spécialités"
                  options={SPECIALITE_OPTIONS.map(value => ({ value, label: value }))}
                />
              </Form.Item>

              <Form.Item label="Enseigne" name="enseignes" className="no-box-shadow">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Sélectionner une ou plusieurs enseignes"
                  options={ENSEIGNE_OPTIONS.map(value => ({ value, label: value }))}
                />
              </Form.Item>

              <Space>
                <Button type="primary" htmlType="submit" loading={saving}>
                  Enregistrer
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14} xl={14}>
          <OrganisationFacturationCgv orgaId={orgaId} orgaSiret={orgaSiret} />
        </Col>
      </Row>
    </div>
  )
}
